import React, { useState, useRef, useCallback } from 'react';
import { fileApi, ChatMode } from '../services/api';
import { useSession } from '../contexts/SessionContext';
import ModeSelector from './ModeSelector';
import LoadingSpinner from './LoadingSpinner';
import './CenteredChatInput.css';
import AssertionLogoFull from '../assets/logos/Assertion Logo & icon-02.svg';

interface CenteredChatInputProps {
  onSendMessage: (message: string, attachedFiles?: any[]) => void;
  sessionId: string;
  onFilesUploaded?: (files: any[]) => void;
  onDraftCapture?: (draft: string) => void;
}

const CenteredChatInput: React.FC<CenteredChatInputProps> = ({ onSendMessage, sessionId, onFilesUploaded, onDraftCapture }) => {
  const { updateSessionMode, currentSession } = useSession();
  const [inputValue, setInputValue] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [localFiles, setLocalFiles] = useState<any[]>([]); // Track files for non-workflow modes
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  
  // Get the current mode from the session
  const currentMode = currentSession?.mode || 'chat';

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('CenteredChatInput handleSubmit called');
    console.log('Input value:', inputValue);
    console.log('Current mode:', currentMode);
    console.log('Local files:', localFiles);
    if (inputValue.trim()) {
      console.log('Calling onSendMessage with:', inputValue.trim(), localFiles);
      // For non-workflow modes, pass files with the message
      if (currentMode !== 'workflow' && localFiles.length > 0) {
        onSendMessage(inputValue.trim(), localFiles);
        setLocalFiles([]); // Clear files after sending
      } else {
        onSendMessage(inputValue.trim());
      }
      setInputValue('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 200)}px`;
    }
  };

  const handleModeChange = async (newMode: ChatMode) => {
    try {
      await updateSessionMode(sessionId, newMode);
    } catch (error) {
      console.error('Failed to update session mode:', error);
    }
  };

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCounter.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;

    // Files are now allowed in all modes

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setIsUploading(true);
      try {
        // Upload files with the session ID
        const uploadSessionId = sessionId;
        
        const uploadPromises = files.map(file => fileApi.upload(file, uploadSessionId));
        const results = await Promise.all(uploadPromises);
        const uploadedFiles = results.map(r => r.data);
        
        // Handle files based on mode
        if (currentMode === 'workflow' && onFilesUploaded) {
          // Capture draft before transitioning
          if (onDraftCapture && inputValue.trim()) {
            onDraftCapture(inputValue);
          }
          // For workflow mode, trigger the callback to push to sidebar
          onFilesUploaded(uploadedFiles);
        } else {
          // For chat/research modes, store files locally
          setLocalFiles(prev => [...prev, ...uploadedFiles]);
        }
        
        // Show success animation
        const successDiv = document.createElement('div');
        successDiv.className = 'file-upload-success';
        successDiv.textContent = `${files.length} file${files.length > 1 ? 's' : ''} uploaded!`;
        document.body.appendChild(successDiv);
        
        setTimeout(() => {
          successDiv.remove();
        }, 3000);
      } catch (error) {
        console.error('Error uploading files:', error);
      } finally {
        setIsUploading(false);
      }
    }
  }, [sessionId, onFilesUploaded, currentMode, onDraftCapture, inputValue]);

  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    try {
      const uploadPromises = Array.from(files).map(file => fileApi.upload(file, sessionId));
      const results = await Promise.all(uploadPromises);
      const uploadedFiles = results.map(r => r.data);
      
      // Handle files based on mode
      if (currentMode === 'workflow' && onFilesUploaded) {
        // Capture draft before transitioning
        if (onDraftCapture && inputValue.trim()) {
          onDraftCapture(inputValue);
        }
        // For workflow mode, trigger the callback to push to sidebar
        onFilesUploaded(uploadedFiles);
      } else {
        // For chat/research modes, store files locally
        setLocalFiles(prev => [...prev, ...uploadedFiles]);
      }
      
      // Show success animation
      const successDiv = document.createElement('div');
      successDiv.className = 'file-upload-success';
      successDiv.textContent = `${files.length} file${files.length > 1 ? 's' : ''} uploaded!`;
      document.body.appendChild(successDiv);
      
      setTimeout(() => {
        successDiv.remove();
      }, 3000);
    } catch (error: any) {
      console.error('Error uploading files:', error);
      if (error.response?.data?.detail) {
        console.error('Server error detail:', error.response.data.detail);
        alert(`File upload failed: ${error.response.data.detail}`);
      } else if (error.response?.data) {
        console.error('Server error data:', error.response.data);
        alert(`File upload failed: ${JSON.stringify(error.response.data).substring(0, 200)}`);
      } else {
        alert(`File upload failed: ${error.message || 'Unknown error'}`);
      }
    } finally {
      setIsUploading(false);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [sessionId, onFilesUploaded, currentMode, onDraftCapture, inputValue]);

  return (
    <div className="centered-chat-input">
      <div className="welcome-section">
        <img src={AssertionLogoFull} alt="Assertion" className="welcome-logo" />
        <p className="welcome-tagline">Purpose-built AI for modern accounting teams</p>
      </div>

      <form onSubmit={handleSubmit} className="chat-form">
        <div 
          className={`input-wrapper chat-mode-${currentMode} ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <div className="mode-selector-container">
            <ModeSelector currentMode={currentMode} onModeChange={handleModeChange} />
          </div>
          {isDragging && (
            <div className="drag-overlay">
              <div className="drag-content">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <p>Drop files here to upload</p>
              </div>
            </div>
          )}
          {isUploading && (
            <div className="upload-progress">
              <div className="upload-spinner"></div>
              <p>Uploading files...</p>
            </div>
          )}
          <textarea
            ref={textareaRef}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              adjustTextareaHeight();
            }}
            onKeyDown={handleKeyPress}
            placeholder={
              currentMode === 'chat' 
                ? "Ask questions or upload files (PDF, Word, images)..."
                : currentMode === 'workflow'
                ? "Upload files or describe your data task..."
                : "Research topics or analyze documents..."
            }
            className="chat-input"
            rows={3}
            autoFocus
          />
          {/* File upload button for all modes */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.csv,.xls,.xlsx"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="file-upload-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            title="Upload files"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button 
            type="submit" 
            className="send-button"
            disabled={!inputValue.trim()}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      </form>

      <div className="keyboard-hint">
        <span>Press <kbd>Enter</kbd> to send, <kbd>Shift + Enter</kbd> for new line</span>
      </div>
      
      {isUploading && (
        <div className="upload-overlay">
          <LoadingSpinner message="Uploading files..." size="medium" />
        </div>
      )}
    </div>
  );
};

export default CenteredChatInput;